import type { MaybeRefOrGetter } from 'vue'
import type {
  QRListResponse,
  QRTransactionGroup,
  QRTransactionListParams,
  QRTransactionListResponse,
} from '~/types/qrStatic'
import { keepPreviousData, useQuery } from '@tanstack/vue-query'

// Get list of available QR options for filter
export async function getQRList(): Promise<QRListResponse> {
  const api = useApi('v1')
  const response = await api.post('billing/qrstatic/report/qrlist')
  return response as QRListResponse
}

// Get transaction list with filters and pagination
export async function getQRTransactionList(
  params: QRTransactionListParams,
): Promise<QRTransactionListResponse> {
  const api = useApi('v1')
  const response = await api.post('billing/qrstatic/report/data', params)

  // Group transactions by date (matching legacy behavior)
  const rawData = response.data
  const grouped: { [key: string]: QRTransactionGroup } = {}

  if (rawData.data && Array.isArray(rawData.data)) {
    rawData.data.forEach((item: any) => {
      // Extract date from created_at
      const dateKey = item.created_at.split('T')[0]

      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          day: dateKey,
          data: [],
        }
      }
      grouped[dateKey].data.push(item)
    })
  }

  // Convert to array and sort by date descending
  const list = Object.values(grouped).sort((a, b) =>
    b.day.localeCompare(a.day),
  )

  return {
    data: {
      list,
      per_page: rawData.per_page || 15,
      current_page: rawData.current_page || 1,
      total: rawData.total || 0,
    },
    meta: response.meta,
  } as QRTransactionListResponse
}

// TanStack Query Hooks

export function useGetQRList() {
  return useQuery({
    queryKey: ['qrStatic', 'list'],
    queryFn: () => getQRList(),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  })
}

export function useGetQRTransactionList(
  params: MaybeRefOrGetter<QRTransactionListParams>,
) {
  return useQuery({
    queryKey: computed(() => ['qrStatic', 'transactions', toValue(params)]),
    queryFn: () => getQRTransactionList(toValue(params)),
    placeholderData: keepPreviousData,
  })
}
